import React from "react";
var __jsx = React.createElement;
import { CDN_LINK } from "@globalConfig";
import { useTranslation } from "@i18n";
import useHelpers from "@hooks/useHelpers";
import useIsBrowser from "@hooks/useIsBrowser";
import { useCart } from "@/common/Contexts/CartContext";
import { trackEvents } from "@/common/events/events";
import { URL_SELL_TRADE_LANDING_v2 } from "@/pages/sell-trade-v2/sell-trade/constants";
import { dispatchSideMenuToggle } from "@/_LayoutsRelated/layoutHooks/useSideMenuController";
import SyarahLogo from "@/components/SyarahLogo/SyarahLogo";
import { cn } from "@/common/lib/utils";
import useNavItemTracker from "../navEvents";
import { SHOW_NOTIFICATION_CENTER } from "../navFeatures";
import MobileListingSearchRow from "./MobileListingSearchRow";
/**
 * 32px outlined circle with an 18px glyph — the desktop header's icon-well
 * scale, adopted here so these sit correctly beside the 83x38 logo. Figma
 * 4121:23783 drew them at 25px/13px against a 48x22 logo.
 * In Figma the bell and cart SVGs carry their own ring and only `search` gets
 * an explicit border; drawing all three as bordered circles is equivalent,
 * since our glyphs are ringless Iconify icons.
 */
var iconButtonClass = "inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-solid border-[#e6e9ef] bg-white p-0 text-navy-primary no-underline";

/** Bare icon, no ring — hamburger and back chevron. */
var bareButtonClass = "inline-flex shrink-0 items-center justify-center border-0 bg-transparent p-0 text-navy-primary";
var IconGlyph = function IconGlyph(_ref) {
  var icon = _ref.icon,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 18 : _ref$size,
    className = _ref.className;
  return /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    className: cn("iconify", icon, className),
    style: {
      width: size,
      height: size
    }
  });
};
var MobileHeader = function MobileHeader(_ref2) {
  var variant = _ref2.variant,
    mobileHasBack = _ref2.mobileHasBack,
    mobileHasTitle = _ref2.mobileHasTitle,
    mobileNoLogo = _ref2.mobileNoLogo,
    logoNotClickable = _ref2.logoNotClickable,
    burgrMenuisCncl = _ref2.burgrMenuisCncl,
    hideBurgerMenu = _ref2.hideBurgerMenu,
    hideSideMenue = _ref2.hideSideMenue,
    tracking = _ref2.tracking,
    showCartIcon = _ref2.showCartIcon,
    showUserAvatar = _ref2.showUserAvatar,
    headerActions = _ref2.headerActions;
  var _useTranslation = useTranslation(["web_header", "web_common", "web_search"]),
    t = _useTranslation.t;
  var _useHelpers = useHelpers(),
    createUrl = _useHelpers.createUrl;
  var _useCart = useCart(),
    totalItems = _useCart.totalItems;
  var isBrowser = useIsBrowser();
  var trackNav = useNavItemTracker("top_navigation");
  var isFunnel = variant === "funnel";
  var showBurger = !isFunnel && !hideSideMenue && !hideBurgerMenu && !tracking;

  /*
   * Prefer the listing's own back link when the page exposes one (unchanged
   * from the previous header), else walk browser history. On a cold entry —
   * shared link, new tab — there is no history to walk, so fall back to home
   * rather than leaving a dead control.
   */
  var goBack = function goBack() {
    var _backToListing$queryS;
    var backToListing = document.querySelector(".backtolisting");
    var href = backToListing === null || backToListing === void 0 ? void 0 : (_backToListing$queryS = backToListing.querySelector("a")) === null || _backToListing$queryS === void 0 ? void 0 : _backToListing$queryS.getAttribute("href");
    if (href) {
      window.location.href = href;
      return;
    }
    if (window.history.length > 1) {
      history.back();
      return;
    }
    window.location.href = createUrl("/");
  };

  /* Rendered unconditionally: the design always shows it, and it now has a
     defined destination in every case. Also means it ships in the SSR HTML
     instead of appearing after hydration. */
  var backButton = /*#__PURE__*/React.createElement("button", {
    type: "button",
    id: "mobile-nav-back",
    "data-id": "mobile-nav-back",
    "aria-label": t("back", {
      ns: "web_common"
    }),
    onClick: function onClick() {
      trackNav("back");
      goBack();
    },
    className: cn(bareButtonClass, "size-6")
  }, /*#__PURE__*/React.createElement(IconGlyph, {
    icon: "material-symbols--chevron-left-rounded",
    size: 24,
    className: "ar:rotate-180"
  }));
  var logo = !mobileNoLogo && /*#__PURE__*/React.createElement("a", {
    href: createUrl("/"),
    "aria-label": "Syarah home",
    id: "mobile-nav-logo",
    "data-id": "mobile-nav-logo",
    onClick: function onClick() {
      return trackNav("logo");
    },
    className: cn("shrink-0", logoNotClickable && "pointer-events-none")
  }, /*#__PURE__*/React.createElement(SyarahLogo, {
    width: 57,
    height: 38,
    className: "hasEvents",
    id: "home-logo"
  }));
  var burger = showBurger && /*#__PURE__*/React.createElement("button", {
    type: "button",
    id: "mobile-nav-menu",
    "data-id": "mobile-nav-menu",
    "aria-label": t("menu", {
      ns: "web_header"
    }),
    onClick: function onClick() {
      trackNav("hamburger_menu");
      dispatchSideMenuToggle();
    },
    className: cn(bareButtonClass, "size-6")
  }, /*#__PURE__*/React.createElement(IconGlyph, {
    icon: "material-symbols--menu-rounded",
    size: 24
  }));

  /* Cart visibility, same rule as the pre-revamp header: the page opts in with
     `showCartIcon` (only home and the oil surfaces do), and there has to be
     something in the cart. Written as `false && ...` like `notifications`, so
     it falls out of whichever group it sits in rather than each variant
     repeating the test.
     `isBrowser` is part of the condition, not just the badge's: the count is
     only known after hydration (`cartCountFromMeta` starts at 0), so server and
     first client render agree on hidden, and the icon arrives with its badge. */
  var cart = showCartIcon && isBrowser && totalItems > 0 && /*#__PURE__*/React.createElement("a", {
    href: createUrl("/site/cart"),
    "aria-label": t("cart", {
      ns: "web_header"
    }),
    id: "mobile-nav-cart",
    "data-id": "mobile-nav-cart",
    className: cn(iconButtonClass, "relative"),
    onClick: function onClick(e) {
      trackNav("cart");
      if (__IS_CSR__ && window.location.pathname === new URL(createUrl("/site/cart"), window.location.origin).pathname) {
        e.preventDefault();
      }
    }
  }, /*#__PURE__*/React.createElement(IconGlyph, {
    icon: "material-symbols--shopping-cart-outline-rounded"
  }), /*#__PURE__*/React.createElement("span", {
    className: "absolute -top-1 -start-1 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-[#EC6234] px-[3px] text-[8px] font-bold leading-none text-white"
  }, totalItems > 99 ? "99+" : totalItems));

  /* Hidden until the notification centre exists — see navFeatures. Same
     `false && ...` shape as `burger` and `cancelButton`, so it simply drops out
     of the group it sits in. */
  var notifications = SHOW_NOTIFICATION_CENTER && /*#__PURE__*/React.createElement("button", {
    type: "button",
    id: "mobile-nav-notifications",
    "data-id": "mobile-nav-notifications",
    onClick: function onClick() {
      return trackNav("notifications");
    },
    "aria-label": t("notifications", {
      ns: "web_header"
    }),
    className: cn(iconButtonClass, "relative cursor-pointer")
  }, /*#__PURE__*/React.createElement(IconGlyph, {
    icon: "material-symbols--notifications-outline-rounded"
  }), /*#__PURE__*/React.createElement("span", {
    className: "absolute -top-px end-0 size-[6px] rounded-full bg-[#F22C2C]"
  }));
  var sellCarCta = /*#__PURE__*/React.createElement("a", {
    href: createUrl(URL_SELL_TRADE_LANDING_v2),
    id: "mobile-nav-sell-car",
    "data-id": "mobile-nav-sell-car",
    onClick: function onClick() {
      trackNav("sell_your_car");
      trackEvents("sell_car_started", {
        referral_location: "top_navigation_cta",
        version: "3"
      });
    },
    className: "inline-flex h-[32px] shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-primary-new px-4 text-xs font-medium leading-[1.5] tracking-[-0.24px] text-[#fbfbfb] no-underline"
  }, t("sell_car", {
    ns: "web_aside_menu"
  }));
  var trackingLink = /*#__PURE__*/React.createElement("a", {
    href: createUrl("/tracking-landing"),
    "data-id": "mobile-nav-tracking",
    onClick: function onClick() {
      trackNav("order_tracking");
      trackEvents("order_tracking_entry", {
        referral_location: "top_navigation"
      });
    },
    className: "flex items-center gap-2 text-base text-[#484848] no-underline"
  }, /*#__PURE__*/React.createElement("img", {
    src: CDN_LINK + "/assets/images/trackinh-header.svg",
    alt: "Tracking icon",
    className: "en:rotate-180"
  }), t("back_track_car", {
    ns: "web_header"
  }));
  var cancelButton = burgrMenuisCncl && /*#__PURE__*/React.createElement("button", {
    type: "button",
    id: "mobile-nav-cancel",
    "data-id": "mobile-nav-cancel",
    onClick: function onClick() {
      trackNav("cancel");
      window.history.back();
    },
    className: "cnclHDR border-0 bg-transparent p-0 text-sm"
  }, t("cancel", {
    ns: "web_common"
  }));
  var avatar = showUserAvatar && /*#__PURE__*/React.createElement("a", {
    href: createUrl("/user/account"),
    "data-id": "mobile-nav-account",
    onClick: function onClick() {
      return trackNav("account", "my_account");
    },
    className: "flex size-8 items-center justify-center"
  }, /*#__PURE__*/React.createElement("img", {
    src: CDN_LINK + "/assets/images/account.svg",
    width: "24px",
    height: "24px",
    alt: "account"
  }));

  /** Figma 4121:23783: 393x64, px-24 py-15. Bottom hairline only — a blurred
      shadow with 0 spread bleeds out of the side edges. */
  var shell = function shell(children) {
    return /*#__PURE__*/React.createElement("div", {
      className: "m-show bg-white shadow-[0_1px_0_0_rgba(0,0,0,0.08)]"
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex h-16 items-center justify-between gap-2 px-6"
    }, children));
  };

  /* ---------- Pre-checkout / checkout funnel: back · title · cancel ---------- */
  if (isFunnel) {
    return shell(/*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "flex min-w-0 flex-1 items-center"
    }, backButton), mobileHasTitle && /*#__PURE__*/React.createElement("p", {
      className: "min-w-0 flex-1 truncate text-center text-sm font-semibold text-navy-primary"
    }, mobileHasTitle), /*#__PURE__*/React.createElement("div", {
      className: "flex min-w-0 flex-1 items-center justify-end"
    }, cancelButton)));
  }

  /* ---------- Tracking pages keep their dedicated back-to-tracking row ---------- */
  if (tracking) {
    return shell(/*#__PURE__*/React.createElement(React.Fragment, null, trackingLink, cancelButton));
  }

  /* ---------- Listing / search — shared row, Figma 4090:12509 ---------- */
  if (variant === "listing") {
    return shell(/*#__PURE__*/React.createElement(MobileListingSearchRow, null));
  }

  /*
   * Home. Figma packs hamburger · CTA · bell · cart · search into a single
   * 8px-gap group at the inline end, with the logo alone at the inline start —
   * not five children spread apart by `justify-between`.
   * The group is ordered in reverse because the first child renders rightmost
   * in RTL, giving visual L→R: burger, CTA, bell, cart, search.
   */
  if (variant === "home") {
    return shell(/*#__PURE__*/React.createElement(React.Fragment, null, logo, /*#__PURE__*/React.createElement("div", {
      className: "flex items-center gap-2"
    }, cart, notifications, sellCarCta, burger)));
  }

  /*
   * Car details — Figma 4121:23862.
   * Inline start (right in RTL): back chevron then logo, 4px apart.
   * Inline end (left): share · favorite · hamburger, 12px apart. Share comes
   * first because the first child sits right-most within the group.
   */
  if (variant === "car-details") {
    return shell(/*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "flex min-w-0 items-center gap-1"
    }, mobileHasBack !== false && backButton, logo), /*#__PURE__*/React.createElement("div", {
      className: "flex items-center gap-3"
    }, headerActions, /*#__PURE__*/React.createElement("div", {
      id: "mobile-header-actions",
      className: "flex items-center gap-2 empty:hidden"
    }), burger)));
  }

  /*
   * Landing / SEO / everything else — Figma 4121:23861: back chevron at the
   * inline start, then the logo, hamburger at the far end.
   *
   * The back arrow is unconditional here (it used to need `mobileHasBack`,
   * which SEO pages do not pass, so they had no way back). `backButton` still
   * self-guards on `history.state`, so it is omitted on a cold entry where
   * there is nowhere to go back to.
   */
  return shell(/*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "flex min-w-0 items-center gap-1"
  }, mobileHasBack !== false && backButton, logo), mobileHasTitle && /*#__PURE__*/React.createElement("p", {
    className: "min-w-0 flex-1 truncate text-center text-sm font-semibold text-navy-primary"
  }, mobileHasTitle), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-2"
  }, cart, avatar, burger)));
};
export default MobileHeader;